import type { PageContext, PartnerAssistantEvent } from "./types";
import { ASSISTANT_API_ORIGIN as ORIGIN } from "./assistantOrigin";

/**
 * SSE помощника партнёра (Task 18). EventSource не умеет POST, поток
 * читается через fetch. В проде бэкенд напрямую (VITE_ASSISTANT_API_ORIGIN,
 * токен Bearer из localStorage "auth-token", который кладёт auth-link
 * Apollo), в деве тот же origin через прокси Vite.
 */

export interface PartnerMessageRequest {
  conversationId: string | null;
  text: string;
  attachmentIds: string[];
  page: PageContext;
  locale: string;
}

export interface SseResult {
  status: number;
  error: string | null;
}

/**
 * Разбирает накопленный буфер на целые блоки "event: ...\ndata: ...\n\n".
 * Незаконченный хвост возвращается в rest. Блоки без data (комментарии
 * ": ping" каждые 15 секунд) и битый JSON пропускаются.
 */
export function parseSseChunk(buffer: string): {
  events: PartnerAssistantEvent[];
  rest: string;
} {
  const events: PartnerAssistantEvent[] = [];
  const parts = buffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = parts.pop() ?? "";
  for (const part of parts) {
    const data = part
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).replace(/^ /, ""))
      .join("\n");
    if (!data) continue;
    try {
      events.push(JSON.parse(data) as PartnerAssistantEvent);
    } catch {
      // битый кусок пропускаем
    }
  }
  return { events, rest };
}

async function postSse(
  path: string,
  body: unknown,
  onEvent: (e: PartnerAssistantEvent) => void,
  signal?: AbortSignal
): Promise<SseResult> {
  const token = window.localStorage.getItem("auth-token");
  const res = await fetch(`${ORIGIN}${path}`, {
    method: "POST",
    credentials: ORIGIN ? "omit" : "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const data = (await res.json().catch(() => ({}))) as { error?: unknown };
    return {
      status: res.status,
      error: typeof data.error === "string" ? data.error : "ERROR",
    };
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSseChunk(buffer);
    buffer = rest;
    events.forEach(onEvent);
  }
  // Последний блок без завершающей пустой строки.
  const { events } = parseSseChunk(`${buffer}${decoder.decode()}\n\n`);
  events.forEach(onEvent);
  return { status: res.status, error: null };
}

export const sendPartnerMessage = (
  body: PartnerMessageRequest,
  onEvent: (e: PartnerAssistantEvent) => void,
  signal?: AbortSignal
) => postSse("/api/assistant/partner/messages", body, onEvent, signal);

export const executePartnerPlan = (
  planId: string,
  confirmedSteps: number[],
  locale: string,
  onEvent: (e: PartnerAssistantEvent) => void,
  signal?: AbortSignal
) =>
  postSse(
    `/api/assistant/partner/plans/${encodeURIComponent(planId)}/execute`,
    { confirmedSteps, locale },
    onEvent,
    signal
  );
